import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Maximize2,
  Minimize2,
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  Settings,
  Volume2,
  Users,
  Gamepad2,
  ChevronUp,
  X,
  MessageSquare,
  Sliders,
  Zap,
  Smile,
  Film,
  Sparkles,
  Image as ImageIcon,
  Headphones,
  HeadphoneOff,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import VideoTile from './VideoTile';
import { AudioProcessor } from '../services/audioProcessing';
import { DEFAULT_BACKGROUNDS, type VideoEffectMode } from '../services/videoProcessing';
import { useCallStore } from '../stores/callStore';
import WatchTogether from './WatchTogether';
import GameManager from '../game/GameManager';
import LeaderboardPanel from './LeaderboardPanel';
import type { GameId, GameManifest } from '../game/framework/types';
import { getGame } from '../game/framework/GamesRegistry';
import { SOUNDBOARD_SOUNDS } from '../constants/defaults';

interface CallLayoutProps {
  vaultId?: string;
  currentUserId: string;
  isPipMode: boolean;
  setIsPipMode: (pip: boolean) => void;
  onReturnToCall?: () => void;
  isCinemaMode?: boolean;
  setIsCinemaMode?: (cinema: boolean) => void;
  pipPosition: { x: number; y: number };
  setPipPosition: (pos: { x: number; y: number }) => void;
  onEndCall: () => void;
  participants: any[];
  activeSpeakerId: string;
  localStream: MediaStream | null;
  screenStream: MediaStream | null;
  remoteScreenStreams?: Record<string, MediaStream>;
  isScreenSharing: boolean;
  toggleScreenShare: (preferWindow?: boolean) => void;
  stopScreenShare: () => void;
  isVideoOn: boolean;
  toggleVideo: () => void;
  isMuted: boolean;
  toggleMute: () => void;
  isDeafened: boolean;
  toggleDeafen: () => void;
  onUserContextMenu: (e: React.MouseEvent, userId: string) => void;
  onSendMessage?: (userId: string, text?: string) => void;
  showSidebars?: boolean;
  toggleSidebars?: () => void;
  isMediaPlayerPoppedOut?: boolean;
  setIsMediaPlayerPoppedOut?: (popped: boolean) => void;
  // Soundboard and Reactions
  sbVolume: number;
  setSbVolume: (v: number) => void;
  onPlaySoundboard: (url: string) => void;
  onSendReaction: (emoji: string) => void;
  reactions: any[];
  remoteStreams: Record<string, MediaStream>;
  // Audio settings props
  audioSettings: {
    isLimiterEnabled: boolean;
    limiterThreshold: number;
    isNormalizerEnabled: boolean;
    normalizerThreshold: number;
    outputGain: number;
    isNoiseSuppressionEnabled: boolean;
  };
  setAudioSettings: (settings: any) => void;
  getUserFontStyle?: (userId: string) => React.CSSProperties | undefined;
  channelId?: string;
  // Optional externally-controlled layout mode. If passed, callers MUST pass
  // `setLayoutMode` as well, otherwise internal writes will be silently
  // ignored (the component falls back to its own internal state when both
  // props are not provided together).
  layoutMode?: 'speaker' | 'focus' | 'tile';
  setLayoutMode?: (mode: 'speaker' | 'focus' | 'tile') => void;
  onWatchTogetherHostChange?: (isHost: boolean) => void;

  // GamesRail integration (psydechat call-channel activities).
  /**
   * Called when the local user clicks Start on a games-rail entry. The
   * parent (App.tsx) is responsible for: (a) updating its `activeGame`
   * state, (b) patching the local presence payload via useRealtimeSync,
   * (c) on game exit, clearing both. See App.tsx for the canonical wiring.
   */
  onLaunchGame?: (gameId: GameId, manifest: GameManifest) => void;
  /**
   * Externally-controlled current game id. When the parent's `activeGame`
   * state flips to a non-null id (because of a rail launch or the legacy
   * GameContainer mount), this prop mirrors that and the embedded
   * GameManager auto-opens that manifest without requiring the user to
   * click through its lobby.
   */
  initialGameId?: GameId | null;
  /**
   * Called when the GameManager exits a game (whether by clicking the
   * activity's Exit button, by the activity falling back to the lobby,
   * or by an error envelope). Parent uses this to clear presence so the
   * voice-channel 🎮 pill decays for other participants.
   */
  onAppGameExit?: () => void;
}

export default function CallLayout({
  vaultId,
  currentUserId,
  isPipMode,
  setIsPipMode,
  onReturnToCall,
  channelId,
  isCinemaMode = false,
  setIsCinemaMode,
  pipPosition,
  setPipPosition,
  onEndCall,
  participants,
  activeSpeakerId,
  localStream,
  screenStream,
  remoteScreenStreams,
  isScreenSharing,
  toggleScreenShare,
  stopScreenShare,
  isVideoOn,
  toggleVideo,
  isMuted,
  toggleMute,
  isDeafened,
  toggleDeafen,
  onUserContextMenu,
  onSendMessage,
  showSidebars = false,
  toggleSidebars,
  isMediaPlayerPoppedOut,
  setIsMediaPlayerPoppedOut,
  sbVolume,
  setSbVolume,
  onPlaySoundboard,
  onSendReaction,
  reactions,
  remoteStreams,
  audioSettings,
  setAudioSettings,
  getUserFontStyle,
  layoutMode: layoutModeProp,
  setLayoutMode: setLayoutModeProp,
  onWatchTogetherHostChange,
  onLaunchGame,
  initialGameId = null,
  onAppGameExit,
}: CallLayoutProps) {
  const [internalLayoutMode, setInternalLayoutMode] = useState<'speaker' | 'focus' | 'tile'>('tile');
  // Allow the parent (App) to control layout mode externally; falls back to
  // the internal state when no controlled props are provided so both
  // controlled and uncontrolled usage work. Controlled usage MUST supply both
  // `layoutMode` and `setLayoutMode` together — providing only one would
  // desync reads from writes (writes mutate a value the reads ignore).
  const layoutMode = layoutModeProp ?? internalLayoutMode;
  const setLayoutMode = (setLayoutModeProp && layoutModeProp)
    ? setLayoutModeProp
    : setInternalLayoutMode;
  const [focusedParticipantId, setFocusedParticipantId] = useState<string | null>(null);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [showScreenShareOptions, setShowScreenShareOptions] = useState(false);
  // Video options submenu — mirrors the screen-share chevron sub-button pattern.
  // Local open/close flag stays here because it's per-component UI state. The
  // background effect fields (`videoEffectMode`, `videoEffectBg`, `videoEffectIntensity`)
  // are hoisted to useCallStore so App.tsx's VideoProcessor lifecycle sees
  // changes without prop drilling — the canvas pipeline produces the actual
  // processed MediaStream that gets swapped via webRTCManager.replaceVideoTrack.
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  const videoEffectMode      = useCallStore((s) => s.videoEffectMode);
  const videoEffectBg        = useCallStore((s) => s.videoEffectBg);
  const videoEffectIntensity = useCallStore((s) => s.videoEffectIntensity);
  const setVideoEffectMode      = useCallStore((s) => s.setVideoEffectMode);
  const setVideoEffectBg        = useCallStore((s) => s.setVideoEffectBg);
  const setVideoEffectIntensity = useCallStore((s) => s.setVideoEffectIntensity);
  const [showSoundboard, setShowSoundboard] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [isWatchTogetherActive, setIsWatchTogetherActive] = useState(false);
  const [isGamesActive, setIsGamesActive] = useState(false);
  // NEURAL_CHAMPIONS leaderboards panel. Independent of isGamesActive —
  // the Trophy button (inside GameManager's own lobby footer) toggles this
  // state; the panel portals to document.body.
  //
  // There used to be a second, separately-styled game picker here
  // (GameMenu.tsx, opened via this button before isGamesActive flipped
  // true) in addition to GameManager's own built-in lobby screen (the one
  // GameManager falls back to whenever selectedGame is null — e.g. right
  // after exiting a game). Two different-looking "game menus" for the same
  // job was confusing (user feedback 2026-08-01: "so there's two game
  // menus... I much prefer the one that showed when I exited Draw
  // Together" — i.e. GameManager's own lobby). The Games button now opens
  // that same lobby directly instead of a separate modal.
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  /**
   * Internal record of the game most recently picked from the rail. We
   * funnel it into `<GameManager initialGameId={...}>` so the activity
   * auto-opens without a lobby detour. We also forward the externally
   * controlled `initialGameId` prop — when the parent's state flips we
   * want to resync. The two paths merge via `effectiveGameId` below.
   */
  const [pendingGameId, setPendingGameId] = useState<GameId | null>(null);
  // When the parent passes a non-null initialGameId and we have nothing
  // pending locally, honor the prop so a re-hello from the parent still
  // lands the user in the chosen activity.
  const effectiveGameId: GameId | null = pendingGameId ?? initialGameId ?? null;
  const [activeMessageTarget, setActiveMessageTarget] = useState<{ id: string, name: string } | null>(null);
  const [messageText, setMessageText] = useState('');

  const handleOpenMessage = (userId: string, userName: string) => {
    setActiveMessageTarget({ id: userId, name: userName });
  };

  const handleSendMessage = () => {
    if (activeMessageTarget && messageText.trim()) {
      onSendMessage?.(activeMessageTarget.id, messageText.trim());
    }
    setActiveMessageTarget(null);
    setMessageText('');
  };

  const REACTION_EMOJIS = ['❤️', '🔥', '😂', '😮', '😢', '👏', '🎉', '👀'];

  const [isDraggingPip, setIsDraggingPip] = useState(false);
  const pipDragOffset = useRef({ x: 0, y: 0 });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Anchor ref for the Video Options dropdown. The outside-click handler
  // uses `videoOptionsRef.current.contains(target)` to leave clicks within
  // the menu alone (so the toggle / slider still respond) while closing
  // the menu when the user clicks anywhere else — anchored on this ref so
  // a future refactor doesn't accidentally lose it.
  const videoOptionsRef = useRef<HTMLDivElement>(null);

  /**
   * GameManager exit handler. Three things in one closure: gates the
   * activity surface, forgets the locally-pending game, and bubbles up
   * so App can clear its `activeGame` and the voice-presence pill
   * decays for other participants.
   */
  const handleGameManagerExit = () => {
    setIsGamesActive(false);
    setPendingGameId(null);
    onAppGameExit?.();
  };

  // Lifecycle ping from the GameComponent itself (e.g. FreeDoomActivity.ping
  // on mount, FreeDoomActivity null on unmount/Exit), and from GameManager's
  // own internal lobby selecting a game. Reconciles activeGame + pendingGameId
  // and bubbles up to App.tsx — the channel that lights up the user's own
  // 🎮 pill regardless of which path opened the activity.
  const handleGameChange = (gameId: GameId | null) => {
    if (gameId) {
      const manifest = getGame(gameId);
      if (!manifest) return;
      setIsGamesActive(true);
      setPendingGameId(gameId);
      onLaunchGame?.(gameId, manifest);
    } else {
      setIsGamesActive(false);
      setPendingGameId(null);
      onAppGameExit?.();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingPip) {
        setPipPosition({
          x: window.innerWidth - e.clientX - pipDragOffset.current.x,
          y: window.innerHeight - e.clientY - pipDragOffset.current.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDraggingPip(false);
    };

    if (isDraggingPip) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPip, setPipPosition]);

  // Outside-click-to-close for the Video Options dropdown. Active only while
  // the menu is open (mounted listener is the cheapest contract here). Two
  // cases we explicitly skip:
  //   1. Clicks inside the dropdown — leave the toggle / slider alone.
  //   2. Clicks on the chevron sub-button — its own onClick already toggles,
  //      so the document handler shouldn't race it (the chevron is marked
  //      with `data-video-options-anchor` for cheap `closest()` matching).
  // The listener is `mousedown` rather than `click` so the close happens
  // before any subsequent click handler in the page fires.
  useEffect(() => {
    if (!showVideoOptions) return;
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (videoOptionsRef.current && videoOptionsRef.current.contains(target)) return;
      if (target.closest('[data-video-options-anchor]')) return;
      setShowVideoOptions(false);
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [showVideoOptions]);

  // Memoize reactions by participant to avoid re-filtering on every render
  const reactionsByParticipant = useMemo(() => {
    const map = new Map<string, typeof reactions>();
    reactions.forEach(r => {
      if (!map.has(r.userId)) map.set(r.userId, []);
      map.get(r.userId)?.push(r);
    });
    return map;
  }, [reactions]);

  const renderParticipants = () => {
    if (layoutMode === 'tile') {
      const remoteScreenShareCount = Object.values(remoteScreenStreams || {}).filter(Boolean).length;
      const participantCount = (participants?.length || 0) + (isScreenSharing ? 1 : 0) + remoteScreenShareCount;
      let gridClass = "grid gap-4 h-full overflow-y-auto content-center p-4 ";

      if (participantCount === 1) gridClass += "grid-cols-1 max-w-4xl mx-auto";
      else if (participantCount === 2) gridClass += "grid-cols-1 md:grid-cols-2 max-w-6xl mx-auto";
      else if (participantCount <= 4) gridClass += "grid-cols-2 max-w-6xl mx-auto";
      else if (participantCount <= 6) gridClass += "grid-cols-2 lg:grid-cols-3";
      else gridClass += "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

      return (
        <div className={gridClass}>
          {participants?.map(p => (
            <div
              key={p.id}
              className="aspect-video cursor-pointer transition-transform hover:scale-[1.02] active:scale-95"
              onClick={() => {
                setFocusedParticipantId(p.id);
                setLayoutMode('focus');
              }}
            >
              <VideoTile
                participant={p}
                isSpeaking={activeSpeakerId === p.id}
                stream={(p.id === 'me' || p.id === currentUserId) ? localStream : remoteStreams[p.id]}
                onContextMenu={onUserContextMenu}
                onSendMessage={(userId) => handleOpenMessage(userId, p.name)}
                isLocal={p.id === 'me' || p.id === currentUserId}
                nameStyle={getUserFontStyle?.(p.id === 'me' ? currentUserId : p.id)}
              />
              {/* Floating Reactions Overlay */}
              <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
                <AnimatePresence>
                  {(reactionsByParticipant.get(p.id) || []).map(r => (
                    <motion.div
                      key={r.id}
                      initial={{ y: 50, opacity: 0, scale: 0.5, x: Math.random() * 40 - 20 }}
                      animate={{ y: -150, opacity: [0, 1, 1, 0], scale: [0.5, 1.5, 1.2, 1], x: Math.random() * 100 - 50 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 3, ease: "easeOut" }}
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 text-4xl filter drop-shadow-lg"
                    >
                      {r.emoji}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))}
          {isScreenSharing && (
            <div
              key="screen"
              className="aspect-video cursor-pointer transition-transform hover:scale-[1.02] active:scale-95"
              onClick={() => {
                setFocusedParticipantId('screen');
                setLayoutMode('focus');
              }}
            >
              <VideoTile
                participant={{ id: 'screen', name: 'Your Screen', type: 'screen' }}
                isSpeaking={false}
                stream={screenStream}
                onContextMenu={() => {}}
                isLocal={true}
              />
            </div>
          )}
          {/* Remote screen shares — each sharer gets their own tile in the grid */}
          {Object.entries(remoteScreenStreams || {}).map(([userId, stream]) => {
            if (!stream) return null;
            const ownerParticipant = participants?.find(p => p.id === userId);
            const ownerName = ownerParticipant?.name || 'Peer';
            return (
              <div
                key={`screen_${userId}`}
                className="aspect-video cursor-pointer transition-transform hover:scale-[1.02] active:scale-95"
                onClick={() => {
                  setFocusedParticipantId(`screen_${userId}`);
                  setLayoutMode('focus');
                }}
              >
                <VideoTile
                  participant={{ id: `screen_${userId}`, name: `${ownerName}'s Screen`, type: 'screen' }}
                  isSpeaking={false}
                  stream={stream}
                  onContextMenu={onUserContextMenu}
                  isLocal={false}
                />
              </div>
            );
          })}
        </div>
      );
    }

    if (layoutMode === 'focus') {
      const focusId = focusedParticipantId || (participants && participants[0]?.id);
      const focusParticipant = focusId === 'screen'
        ? { id: 'screen', name: 'Your Screen', type: 'screen' }
        : focusId?.startsWith('screen_')
          ? { id: focusId, name: 'Screen Share', type: 'screen' }
          : (participants?.find(p => p.id === focusId));

      const getFocusStream = () => {
         if (focusParticipant?.id === 'me' || focusParticipant?.id === currentUserId) return localStream;
         if (focusParticipant?.id === 'screen') return screenStream;
         if (focusParticipant?.id.startsWith('screen_')) {
            const pid = focusParticipant.id.replace('screen_', '');
            return remoteScreenStreams?.[pid];
         }
         return remoteStreams?.[focusParticipant?.id || ''];
      };

      return (
        <div className="flex flex-col md:flex-row h-full gap-2 md:gap-4 overflow-hidden">
          <div
            className="flex-grow min-h-0 bg-black/50 rounded-3xl border border-white/10 overflow-hidden relative shadow-2xl cursor-pointer hover:brightness-110 transition-all"
            onClick={() => setLayoutMode('tile')}
            title="Return to Tile view"
          >
            {focusParticipant && (
              <VideoTile
                participant={focusParticipant}
                isSpeaking={activeSpeakerId === focusParticipant.id}
                stream={getFocusStream()}
                onContextMenu={onUserContextMenu}
                onSendMessage={(userId) => handleOpenMessage(userId, focusParticipant.name)}
                hideName={true}
                large={true}
                isLocal={focusParticipant.id === 'me' || focusParticipant.id === currentUserId || focusParticipant.id === 'screen'}
                nameStyle={getUserFontStyle?.(focusParticipant.id === 'me' ? currentUserId : focusParticipant.id)}
              />
            )}

            {/* Floating Reactions Overlay */}
            <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
              <AnimatePresence>
                {reactions.filter(r => r.userId === focusParticipant?.id).map(r => (
                  <motion.div
                    key={r.id}
                    initial={{ y: 50, opacity: 0, scale: 0.5, x: Math.random() * 40 - 20 }}
                    animate={{ y: -250, opacity: [0, 1, 1, 0], scale: [0.5, 2.5, 2, 1], x: Math.random() * 200 - 100 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 4, ease: "easeOut" }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 text-8xl filter drop-shadow-2xl"
                  >
                    {r.emoji}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
          <div className="h-24 md:h-auto md:w-48 flex flex-row md:flex-col gap-2 md:gap-4 overflow-x-auto md:overflow-y-auto pb-2 md:pb-0 md:pr-2 shrink-0">
            {participants?.filter(p => p.id !== focusId).map(p => (
              <React.Fragment key={p.id}>
              <div className="w-32 md:w-full aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125 relative group" onClick={() => setFocusedParticipantId(p.id)}>
                <VideoTile
                  participant={p}
                  isSpeaking={activeSpeakerId === p.id}
                  stream={(p.id === 'me' || p.id === currentUserId) ? localStream : remoteStreams[p.id]}
                  onContextMenu={onUserContextMenu}
                  onSendMessage={(userId) => handleOpenMessage(userId, p.name)}                isLocal={p.id === 'me' || p.id === currentUserId}
                nameStyle={getUserFontStyle?.(p.id === 'me' ? currentUserId : p.id)}
                />
                {/* Small floating reactions for side tiles */}
                <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
                  <AnimatePresence>
                    {reactions.filter(r => r.userId === p.id).map(r => (
                      <motion.div
                        key={r.id}
                        initial={{ y: 20, opacity: 0, scale: 0.5 }}
                        animate={{ y: -60, opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1, 1] }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 2.5 }}
                        className="absolute bottom-2 left-1/2 -translate-x-1/2 text-2xl"
                      >
                        {r.emoji}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
              {remoteScreenStreams?.[p.id] && focusId !== `screen_${p.id}` && (
                <div className="w-32 md:w-full aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125 relative group border-2 border-[var(--neon)] rounded-xl overflow-hidden" onClick={() => { setFocusedParticipantId(`screen_${p.id}`); setLayoutMode('focus'); }}>
                  <VideoTile
                    participant={{ id: `screen_${p.id}`, name: `${p.name}'s Screen`, type: 'screen' }}
                    isSpeaking={false}
                    stream={remoteScreenStreams[p.id]}
                    onContextMenu={onUserContextMenu}
                    isCinemaMode={isCinemaMode}                    onToggleCinemaMode={() => setIsCinemaMode?.(!isCinemaMode)}
                  />
                </div>
              )}
              </React.Fragment>
            ))}
            {isScreenSharing && focusId !== 'screen' && (
              <div key="screen" className="w-32 md:w-full aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125" onClick={() => setFocusedParticipantId('screen')}>
                <VideoTile
                  participant={{ id: 'screen', name: 'Your Screen', type: 'screen' }}
                  isSpeaking={false}
                  stream={screenStream}
                  onContextMenu={() => {}}
                  isCinemaMode={isCinemaMode}                    onToggleCinemaMode={() => setIsCinemaMode?.(!isCinemaMode)}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    // Speaker mode
    const speakerId = activeSpeakerId || (participants && participants[0]?.id);
    const speakerParticipant = participants?.find(p => p.id === speakerId) || (participants && participants[0]);

    return (
      <div className="flex flex-col h-full gap-4 overflow-hidden">
        <div
          className="flex-grow bg-black/50 rounded-3xl border border-white/10 overflow-hidden relative shadow-2xl cursor-pointer"
          onClick={() => { speakerParticipant && setFocusedParticipantId(speakerParticipant.id); setLayoutMode('focus'); }}
        >
          {speakerParticipant && (
            <VideoTile
              participant={speakerParticipant}
              isSpeaking={true}
              stream={(speakerParticipant.id === 'me' || speakerParticipant.id === currentUserId) ? localStream : remoteStreams[speakerParticipant.id]}
              onContextMenu={onUserContextMenu}
              onSendMessage={(userId) => handleOpenMessage(userId, speakerParticipant.name)}
              hideName={true}
              large={true}                isLocal={speakerParticipant.id === 'me' || speakerParticipant.id === currentUserId}
                nameStyle={getUserFontStyle?.(speakerParticipant.id === 'me' ? currentUserId : speakerParticipant.id)}
            />
          )}

          {/* Floating Reactions Overlay for Speaker */}
          <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
            <AnimatePresence>
              {reactions.filter(r => r.userId === speakerParticipant?.id).map(r => (
                <motion.div
                  key={r.id}
                  initial={{ y: 50, opacity: 0, scale: 0.5, x: Math.random() * 40 - 20 }}
                  animate={{ y: -250, opacity: [0, 1, 1, 0], scale: [0.5, 2.5, 2, 1], x: Math.random() * 200 - 100 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 4, ease: "easeOut" }}
                  className="absolute bottom-8 left-1/2 -translate-x-1/2 text-8xl filter drop-shadow-2xl"
                >
                  {r.emoji}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <div className="h-32 shrink-0 flex gap-4 overflow-x-auto pb-2">
          {participants?.filter(p => p.id !== speakerParticipant?.id).map(p => (
            <React.Fragment key={p.id}>
            <div className="w-48 aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125 relative group" onClick={() => { setFocusedParticipantId(p.id); setLayoutMode('focus'); }}>
              <VideoTile
                participant={p}
                isSpeaking={false}
                stream={(p.id === 'me' || p.id === currentUserId) ? localStream : remoteStreams[p.id]}
                onContextMenu={onUserContextMenu}
                onSendMessage={(userId) => handleOpenMessage(userId, p.name)}
                isLocal={p.id === 'me' || p.id === currentUserId}
                nameStyle={getUserFontStyle?.(p.id === 'me' ? currentUserId : p.id)}
              />
              {/* Small floating reactions for bottom row */}
              <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
                <AnimatePresence>
                  {reactions.filter(r => r.userId === p.id).map(r => (
                    <motion.div
                      key={r.id}
                      initial={{ y: 20, opacity: 0, scale: 0.5 }}
                      animate={{ y: -60, opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1, 1] }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 2.5 }}
                      className="absolute bottom-2 left-1/2 -translate-x-1/2 text-2xl"
                    >
                      {r.emoji}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
            {remoteScreenStreams?.[p.id] && speakerId !== `screen_${p.id}` && (
              <div className="w-48 aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125 relative group border-2 border-[var(--neon)] rounded-xl overflow-hidden" onClick={() => { setFocusedParticipantId(`screen_${p.id}`); setLayoutMode('focus'); }}>
                <VideoTile
                  participant={{ id: `screen_${p.id}`, name: `${p.name}'s Screen`, type: 'screen' }}
                  isSpeaking={false}
                  stream={remoteScreenStreams[p.id]}
                  onContextMenu={onUserContextMenu}
                  isCinemaMode={isCinemaMode}                    onToggleCinemaMode={() => setIsCinemaMode?.(!isCinemaMode)}
                />
              </div>
            )}
            </React.Fragment>
          ))}
          {isScreenSharing && speakerId !== 'screen' && (
            <div key="screen" className="w-48 aspect-video shrink-0 cursor-pointer transition-all hover:brightness-125" onClick={() => { setFocusedParticipantId('screen'); setLayoutMode('focus'); }}>
              <VideoTile
                participant={{ id: 'screen', name: 'Your Screen', type: 'screen' }}
                isSpeaking={false}
                stream={screenStream}
                onContextMenu={() => {}}
                isCinemaMode={isCinemaMode}
                onToggleCinemaMode={() => setIsCinemaMode?.(!isCinemaMode)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        style={isPipMode ? { right: `${pipPosition.x}px`, bottom: `${pipPosition.y}px` } : {}}
        className={`${isPipMode ? 'hidden md:flex flex-col fixed w-80 aspect-video z-[60] shadow-2xl rounded-2xl overflow-hidden border-2 border-[var(--neon)] animate-in slide-in-from-bottom-10 bg-black' : 'w-full h-full flex flex-col p-4 bg-black/40 rounded-2xl border border-white/10'}`}
      >
      {isPipMode && (
        <>
          <div
            className="absolute top-0 left-0 right-0 h-10 z-50 cursor-move flex items-center justify-center group"
            onMouseDown={(e) => {
              setIsDraggingPip(true);
              pipDragOffset.current = {
                x: window.innerWidth - e.clientX - pipPosition.x,
                y: window.innerHeight - e.clientY - pipPosition.y
              };
            }}
          >
            <div className="w-12 h-1 bg-white/20 rounded-full group-hover:bg-[var(--neon)] transition-colors" />
          </div>

          <div className="absolute top-2 left-2 z-[60] flex items-center gap-1">
            <button
              onClick={toggleMute}
              className={`p-1.5 rounded-lg border transition-all ${isMuted ? 'bg-red-500/20 border-red-500/30 text-red-500' : 'bg-black/60 border-white/10 text-white/60 hover:text-white'}`}
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <MicOff size={14} /> : <Mic size={14} />}
            </button>
          </div>
          <button
            onClick={() => onReturnToCall ? onReturnToCall() : setIsPipMode(false)}
            className="absolute top-2 right-2 z-[60] p-1.5 bg-black/60 rounded-lg border border-white/10 text-white hover:text-[var(--neon)] hover:bg-black/80 transition-all flex items-center gap-1.5"
            title="Return to Call"
          >
            <Maximize2 size={14} />
            <span className="text-[9px] font-bold uppercase tracking-tight pr-1">Return</span>
          </button>
        </>
      )}

      {/*
        This used to carry `drag="y"` as a swipe-down-to-minimize gesture,
        but framer-motion's drag listener captures pointer-down/move
        anywhere in the subtree — including mouse-drag interactions meant
        for content inside it (drawing on the DrawTogether canvas, any
        click-and-drag elsewhere in the call view). Any such drag that
        crossed the 100px y-offset threshold collapsed the whole call into
        PiP mode, i.e. "the call UI vanishes" mid-draw. There's already an
        explicit Picture-in-Picture button in the call control bar (below)
        and no visible handle for this gesture in full-screen mode, so it
        was a pure accident magnet with no compensating affordance — removed
        rather than scoped to a dedicated handle.
      */}
      <motion.div
        className="w-full h-full flex flex-col gap-4 min-h-0"
      >
        {(() => {
          const focusActive = isWatchTogetherActive || isGamesActive;
          const showSidebar = !isPipMode && focusActive;

          return (
            <div className={`flex-grow min-h-0 relative ${showSidebar ? 'flex gap-2 md:gap-4 overflow-hidden px-2 md:px-4 h-full' : 'pb-44 md:pb-60'}`}>
              <div className={`relative ${showSidebar ? 'flex-1 min-w-0 h-full overflow-hidden' : 'w-full h-full'}`}>
                <div className={(focusActive) ? 'hidden' : 'w-full h-full'}>
                  {renderParticipants()}
                </div>
                {isWatchTogetherActive && (
                  <div className="absolute inset-0 p-2 pb-20 md:pb-24 z-20">
                    <WatchTogether
                      vaultId={vaultId || 'global'}
                      channelId={channelId || 'vc'}
                      currentUserId={currentUserId}
                      currentUsername={participants.find(p => p.id === currentUserId)?.name || 'Guest'}
                      participants={participants}
                      onHostStatusChange={onWatchTogetherHostChange}
                    />
                  </div>
                )}
                {isGamesActive && (
                  <div className="absolute inset-0 p-2 pb-20 md:pb-24 z-20">
                    <GameManager
                      initialRoomId={channelId || 'vc'}
                      initialGameId={effectiveGameId}
                      playerSelf={{ userId: currentUserId }}
                      onExit={handleGameManagerExit}
                      onChange={handleGameChange}
                      onLaunchLeaderboard={() => {
                        setIsGamesActive(false);
                        setIsLeaderboardOpen(true);
                      }}
                    />
                  </div>
                )}

              </div>
              {showSidebar && (
                <aside
                  aria-label="Call participants while game is focused"
                  className={`shrink-0 h-full overflow-y-auto pr-1 pb-20 md:pb-24 z-10 flex flex-col gap-2 pt-2 border-l border-white/10 pl-2 bg-black/40 rounded-2xl relative transition-all duration-300 ${
                    isSidebarCollapsed ? 'w-12 items-center' : 'w-32 sm:w-44 md:w-52 lg:w-60'
                  }`}
                >
                  {!isSidebarCollapsed && participants?.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleOpenMessage(p.id, p.name)}
                      title={`Message ${p.name}`}
                      aria-label={`Open message panel with ${p.name}`}
                      className="aspect-video w-full rounded-xl overflow-hidden border border-white/10 bg-black shrink-0 cursor-pointer transition-transform hover:scale-[1.02] active:scale-95 relative group"
                    >
                      <VideoTile
                        participant={p}
                        isSpeaking={activeSpeakerId === p.id}
                        stream={(p.id === 'me' || p.id === currentUserId) ? localStream : remoteStreams[p.id]}
                        onContextMenu={onUserContextMenu}
                        onSendMessage={(userId) => handleOpenMessage(userId, p.name)}
                        isLocal={p.id === 'me' || p.id === currentUserId}
                        nameStyle={getUserFontStyle?.(p.id === 'me' ? currentUserId : p.id)}
                      />
                    </button>
                  ))}
                  
                  <div className="mt-auto sticky bottom-0 pt-2 pb-2 bg-black/40 backdrop-blur-md border-t border-white/10 flex justify-center w-full z-20">
                    <button 
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                      className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all flex items-center justify-center w-full group"
                      title={isSidebarCollapsed ? "Show Participants" : "Hide Participants"}
                    >
                      {isSidebarCollapsed ? (
                        <Users size={16} className="text-white/60 group-hover:text-white shrink-0" />
                      ) : (
                        <span className="flex items-center gap-2 text-white/60 group-hover:text-white">
                          <Users size={16} className="shrink-0" /> 
                          <span className="text-xs font-bold uppercase tracking-wider">Hide</span>
                        </span>
                      )}
                    </button>
                  </div>
                </aside>
              )}
            </div>
          );
        })()}

        {!isPipMode && (
          <div className={`absolute bottom-2 md:bottom-6 left-1/2 -translate-x-1/2 z-[100] p-2.5 sm:p-3 md:p-4 rounded-2xl md:rounded-3xl max-w-[calc(100%-1rem)] md:max-w-[50rem] w-auto h-auto flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 md:gap-3 transition-all duration-500 hover:opacity-100 hover:translate-y-0 ${isCinemaMode ? 'bg-transparent border-transparent shadow-none backdrop-blur-none opacity-20 hover:opacity-100' : 'bg-black/85 md:bg-black/60 backdrop-blur-3xl border border-white/10 shadow-2xl'} ${isWatchTogetherActive ? 'opacity-30 scale-90 translate-y-4 hover:scale-100 hover:opacity-100' : 'opacity-100 translate-y-0'}`}>
            <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0">
              <button
                onClick={toggleVideo}
                className={`w-full h-full rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${
                  !isVideoOn
                    ? 'bg-red-500 text-white glow-danger hover:bg-red-600'
                    : videoEffectMode !== 'none'
                      ? 'bg-[var(--neon)] text-black glow-active'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                }`}
                title={isVideoOn ? 'Camera on' : 'Camera off'}
              >
                {isVideoOn ? <Video size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" /> : <VideoOff size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />}
              </button>
              <button
                onClick={() => setShowVideoOptions(!showVideoOptions)}
                data-video-options-anchor
                aria-label="Video options"
                aria-expanded={showVideoOptions}
                aria-controls="video-options-menu"
                title="Video options"
                className={`absolute -top-1 -right-1 bg-black/80 rounded-full p-1 border transition-all ${
                  showVideoOptions
                    ? 'border-[var(--neon)] bg-[var(--neon)] text-black glow-active'
                    : 'border-white/10 text-white hover:bg-[var(--neon)] hover:text-black'
                }`}
              >
                <ChevronUp size={10} />
              </button>

              {showVideoOptions && (
                <div
                  ref={videoOptionsRef}
                  id="video-options-menu"
                  role="dialog"
                  aria-label="Video options"
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 bg-black/95 backdrop-blur-2xl border border-white/10 rounded-2xl py-3 shadow-2xl animate-in slide-in-from-bottom-4 z-50"
                >
                  <div className="px-4 py-2 mb-1 border-b border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles size={11} className="text-[var(--neon)]" />
                      <span className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em]">Video Options</span>
                    </div>
                    <button
                      onClick={() => setShowVideoOptions(false)}
                      className="text-white/40 hover:text-white transition-colors p-0.5"
                      title="Close"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div
                    className="px-2 py-2 flex items-center gap-1 bg-black/50 rounded-xl mx-1 border border-white/5"
                    role="radiogroup"
                    aria-label="Video background mode"
                  >
                    {(['none', 'blur', 'picture'] as VideoEffectMode[]).map((mode) => {
                      const active = videoEffectMode === mode;
                      return (
                        <button
                          key={mode}
                          role="radio"
                          aria-checked={active}
                          onClick={() => {
                            setVideoEffectMode(mode);
                            if (mode === 'none') {
                              setShowVideoOptions(false);
                            }
                          }}
                          className={`flex-1 px-2 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${
                            active
                              ? 'bg-[var(--neon)] text-black glow-active'
                              : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          {mode === 'none' ? 'None' : mode === 'blur' ? 'Soft Focus' : 'Picture'}
                        </button>
                      );
                    })}
                  </div>

                  {videoEffectMode === 'blur' && (
                    <div className="px-3 py-3 border-t border-white/5 flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] text-white/30 font-black uppercase tracking-widest">Intensity</span>
                        <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{videoEffectIntensity.toFixed(1)}px</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        step="1"
                        value={videoEffectIntensity}
                        onChange={(e) => setVideoEffectIntensity(Number(e.target.value))}
                        disabled={!isVideoOn}
                        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)] disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    </div>
                  )}

                  {videoEffectMode === 'picture' && (
                    <div className="px-3 py-3 border-t border-white/5 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-white/30 font-black uppercase tracking-widest">
                          Pick a Background
                        </span>
                        <ImageIcon size={11} className="text-white/30" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {DEFAULT_BACKGROUNDS.map((bg) => {
                          const selected = videoEffectBg === bg.src
                            || (!videoEffectBg && bg.src === DEFAULT_BACKGROUNDS[0].src);
                          return (
                            <button
                              key={bg.id}
                              onClick={() => setVideoEffectBg(bg.src)}
                              title={bg.label}
                              data-testid={`bg-thumb-${bg.id}`}
                              className={`relative aspect-video overflow-hidden rounded-lg border transition-all ${
                                selected
                                  ? 'border-[var(--neon)] glow-active scale-[1.02]'
                                  : 'border-white/10 hover:border-white/30 hover:scale-[1.02]'
                              }`}
                            >
                              <img
                                src={bg.src}
                                alt={bg.label}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              {selected && (
                                <div className="absolute inset-0 bg-[var(--neon)]/20 flex items-center justify-center pointer-events-none">
                                  <span className="text-[9px] font-black text-black bg-[var(--neon)] px-1.5 py-0.5 rounded uppercase tracking-wider">
                                    Selected
                                  </span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="px-4 py-2 mt-1 border-t border-white/5">
                    <span className="text-[9px] text-white/30 leading-tight block">
                      Peers will see whatever mode you pick here. Click outside to close.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={toggleMute}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${isMuted ? 'bg-red-500 text-white glow-danger hover:bg-red-600' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <MicOff size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" /> : <Mic size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />}
            </button>

            <button
              onClick={toggleDeafen}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${isDeafened ? 'bg-red-500 text-white glow-danger hover:bg-red-600' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title={isDeafened ? 'Undeafen' : 'Deafen'}
            >
              {isDeafened ? <HeadphoneOff size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" /> : <Headphones size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />}
            </button>

            <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0">
              <button
                onClick={() => setShowScreenShareOptions(!showScreenShareOptions)}
                className={`w-full h-full rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${isScreenSharing ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
                title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
              >
                <Monitor size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
                <div className="absolute -top-1 -right-1 bg-black/80 rounded-full p-0.5 border border-white/10 text-white">
                  <ChevronUp size={10} />
                </div>
              </button>

              {showScreenShareOptions && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-56 bg-black/95 backdrop-blur-2xl border border-white/10 rounded-2xl py-3 shadow-2xl animate-in slide-in-from-bottom-4 z-50">
                  <div className="px-4 py-2 mb-1 border-b border-white/5">
                    <span className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em]">Sharing Options</span>
                  </div>
                  <button
                    onClick={() => toggleScreenShare(false)}
                    className="w-full px-4 py-3 text-left text-xs text-white/70 hover:bg-[var(--neon)] hover:text-black transition-all flex items-center gap-3"
                  >
                    <Monitor size={16} /> Entire Screen
                  </button>
                  <button
                    onClick={() => toggleScreenShare(true)}
                    className="w-full px-4 py-3 text-left text-xs text-white/70 hover:bg-[var(--neon)] hover:text-black transition-all flex items-center gap-3"
                  >
                    <Monitor size={16} /> Specific Window
                  </button>
                  {isScreenSharing && (
                    <button
                      onClick={stopScreenShare}
                      className="w-full px-4 py-3 text-left text-xs text-red-500 hover:bg-red-500 hover:text-white transition-all border-t border-white/5 mt-2 font-bold"
                    >
                      Stop Sharing
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => { setShowReactions(!showReactions); setShowSoundboard(false); setShowAudioSettings(false); }}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${showReactions ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title="Reactions"
            >
              <Smile size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            <button
              onClick={() => { setShowSoundboard(!showSoundboard); setShowReactions(false); setShowAudioSettings(false); }}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${showSoundboard ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title="Soundboard"
            >
              <Volume2 size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            <button
              onClick={() => { setIsWatchTogetherActive(!isWatchTogetherActive); setIsGamesActive(false); setShowSoundboard(false); setShowReactions(false); setShowAudioSettings(false); }}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${isWatchTogetherActive ? 'bg-[var(--neon)] text-black glow-active animate-pulse-slow' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title="Watch Together"
            >
              <Film size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            {/* Games: TOGGLES the NEURAL_ARCADE activity surface. It used to
                only ever open it, so the sole way back out was hunting for an
                in-game EXIT button — pressing the games button again did
                nothing. Now it closes whatever is open (lobby or a running
                activity) via the same teardown path EXIT uses, so App's
                activeGame clears and the 🎮 presence pill decays for peers. */}
            <div className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0">
              <button
                type="button"
                onClick={() => (isGamesActive ? handleGameManagerExit() : setIsGamesActive(true))}
                className={`w-full h-full rounded-xl md:rounded-2xl border flex items-center justify-center transition-all shadow-xl group ${
                  isGamesActive
                    ? 'bg-[var(--neon)] text-black border-transparent glow-active'
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                }`}
                title={isGamesActive ? 'Close games' : 'Games'}
                aria-label={isGamesActive ? 'Close games browser' : 'Open games browser'}
                aria-pressed={isGamesActive}
                data-games-trigger
              >
                <Gamepad2 size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
              </button>
            </div>

            {/* Leaderboards button removed from the call-control ring — it
                now lives inside the GameMenu modal so non-gamers don't see
                it in their call chrome. The legacy NEURAL_CHAMPIONS panel
                is still reachable via the Games menu's footer Trophy CTA. */}

            <button
              onClick={() => setShowAudioSettings(!showAudioSettings)}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${showAudioSettings ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title="Audio Settings"
            >
              <Settings size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            {/* Soundboard Panel */}
            {showSoundboard && (
              <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-80 bg-black/95 backdrop-blur-3xl border border-white/10 rounded-[2rem] p-6 flex flex-col gap-5 animate-in slide-in-from-bottom-4 z-50 shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/5 pb-3">
                  <div className="flex items-center gap-2">
                    <Volume2 size={14} className="text-[var(--neon)]" />
                    <span className="text-[10px] font-black text-white/90 uppercase tracking-[0.3em]">Soundboard</span>
                  </div>
                  <button onClick={() => setShowSoundboard(false)} className="p-1 hover:bg-white/5 rounded-full transition-colors"><X size={16} className="text-white/40" /></button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {SOUNDBOARD_SOUNDS.map(sound => (
                    <button
                      key={sound.id}
                      onClick={() => onPlaySoundboard(sound.url)}
                      className="mobile-full-target flex flex-col items-center gap-1.5 p-3 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 hover:border-[var(--neon)]/30 transition-all group"
                    >
                      <span className="text-xl group-hover:scale-125 transition-transform">{sound.icon}</span>
                      <span className="text-[9px] font-bold text-white/40 uppercase tracking-tighter truncate w-full text-center">{sound.name}</span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-col gap-2 px-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] text-white/20 font-black uppercase tracking-widest">Master Volume</span>
                    <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{Math.round(sbVolume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={sbVolume}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (import.meta.env.DEV) console.log('Setting volume to:', v);
                      setSbVolume(v);
                    }}
                    className="w-full h-1.5 bg-white/5 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                  />
                </div>
              </div>
            )}

            {/* Reactions Panel */}
            {showReactions && (
              <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-black/95 backdrop-blur-3xl border border-white/10 rounded-full px-4 py-2 flex items-center gap-3 animate-in slide-in-from-bottom-4 z-50 shadow-2xl">
                {REACTION_EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onSendReaction(emoji);
                      setShowReactions(false);
                    }}
                    className="mobile-full-target text-2xl hover:scale-150 active:scale-95 transition-transform p-2"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={toggleSidebars}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${showSidebars ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title={showSidebars ? 'Hide Chat' : 'Show Chat'}
            >
              <MessageSquare size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            <button
              onClick={() => setIsPipMode(!isPipMode)}
              className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shadow-xl group ${isPipMode ? 'bg-[var(--neon)] text-black glow-active' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
              title={isPipMode ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'}
            >
              <Minimize2 size={20} className="group-hover:scale-110 transition-transform md:w-[24px] md:h-[24px]" />
            </button>

            <button
              onClick={onEndCall}
              className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center bg-red-500 text-white hover:bg-red-600 transition-all shadow-[0_10px_30px_rgba(239,68,68,0.5)] active:scale-95 group"
              title="Leave Call"
            >
              <PhoneOff size={22} className="group-hover:rotate-[135deg] transition-transform duration-300 md:w-[26px] md:h-[26px]" />
            </button>

            {/* Audio Settings Panel — Three toggle rows + per-row threshold slider
                when enabled. Output Gain is the global ceiling on top. The
                structure here matches the pre-router audio panel that lived
                in this exact slot before the rewrite. */}
            {showAudioSettings && (
              <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-72 bg-black/95 backdrop-blur-3xl border border-white/10 rounded-[2rem] p-6 flex flex-col gap-5 animate-in slide-in-from-bottom-4 z-50 shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/5 pb-3">
                  <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">Neural Audio</span>
                  <button onClick={() => setShowAudioSettings(false)} className="p-1 hover:bg-white/5 rounded-full transition-colors"><X size={16} className="text-white/40" /></button>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Suppression row */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-white/5">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-white tracking-tight">Suppression</span>
                      <span className="text-[9px] text-white/30 uppercase">AI Noise Filtering</span>
                    </div>
                    <button
                      onClick={() => setAudioSettings({ ...audioSettings, isNoiseSuppressionEnabled: !audioSettings.isNoiseSuppressionEnabled })}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${audioSettings.isNoiseSuppressionEnabled ? 'bg-[var(--neon)]' : 'bg-white/10'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transition-all duration-300 ${audioSettings.isNoiseSuppressionEnabled ? 'left-7' : 'left-1'}`}></div>
                    </button>
                  </div>

                  {/* Limiter row */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-white/5">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-white tracking-tight">Limiter</span>
                      <span className="text-[9px] text-white/30 uppercase">Peak Protection</span>
                    </div>
                    <button
                      onClick={() => setAudioSettings({ ...audioSettings, isLimiterEnabled: !audioSettings.isLimiterEnabled })}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${audioSettings.isLimiterEnabled ? 'bg-[var(--neon)]' : 'bg-white/10'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transition-all duration-300 ${audioSettings.isLimiterEnabled ? 'left-7' : 'left-1'}`}></div>
                    </button>
                  </div>

                  {/* Normalizer row */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-white/5">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-white tracking-tight">Normalizer</span>
                      <span className="text-[9px] text-white/30 uppercase">Loudness Consistency</span>
                    </div>
                    <button
                      onClick={() => setAudioSettings({ ...audioSettings, isNormalizerEnabled: !audioSettings.isNormalizerEnabled })}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${audioSettings.isNormalizerEnabled ? 'bg-[var(--neon)]' : 'bg-white/10'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-lg transition-all duration-300 ${audioSettings.isNormalizerEnabled ? 'left-7' : 'left-1'}`}></div>
                    </button>
                  </div>
                </div>

                {/* Limiter threshold slider — visible only when limiter is ON */}
                {audioSettings.isLimiterEnabled && (
                  <div className="flex flex-col gap-2 px-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] text-white/30 font-black uppercase tracking-widest">Limiter Threshold</span>
                      <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{audioSettings.limiterThreshold} dB</span>
                    </div>
                    <input
                      type="range"
                      min="-24"
                      max="0"
                      step="1"
                      value={audioSettings.limiterThreshold}
                      onChange={(e) => setAudioSettings({ ...audioSettings, limiterThreshold: Number(e.target.value) })}
                      className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                    />
                  </div>
                )}

                {/* Normalizer threshold slider — visible only when normalizer is ON */}
                {audioSettings.isNormalizerEnabled && (
                  <div className="flex flex-col gap-2 px-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] text-white/30 font-black uppercase tracking-widest">Normalizer Threshold</span>
                      <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{audioSettings.normalizerThreshold} dB</span>
                    </div>
                    <input
                      type="range"
                      min="-48"
                      max="-6"
                      step="1"
                      value={audioSettings.normalizerThreshold}
                      onChange={(e) => setAudioSettings({ ...audioSettings, normalizerThreshold: Number(e.target.value) })}
                      className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                    />
                  </div>
                )}

                {/* Output Gain is global — always visible */}
                <div className="flex flex-col gap-2 px-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] text-white/30 font-black uppercase tracking-widest">Output Gain</span>
                    <span className="text-[10px] font-mono text-[var(--neon)] font-bold">{Math.round(audioSettings.outputGain * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.05"
                    value={audioSettings.outputGain}
                    onChange={(e) => setAudioSettings({ ...audioSettings, outputGain: Number(e.target.value) })}
                    className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
      </div>
      {isLeaderboardOpen && (
        <LeaderboardPanel
          onClose={() => setIsLeaderboardOpen(false)}
          currentUserId={currentUserId}
        />
      )}
    </>
  );
}

